import { CAMPAIGN_MAX_SIZE } from '@common/constants';
import { ErrorType } from '@common/types/error';
import { clearCampaignTabFromUrl } from '@campaign/utils/campaignTab';

import type {
  NetworkAllCampaigns,
  NetworkCampaign,
} from '@common/types/campaign';
import * as authUtil from '@campaign/domain/middlewares/auth/utils';
import { Store } from '@campaign/domain/store';
import * as reducers from '@campaign/domain/store/reducers';
import * as network from '@campaign/side-effects/network';
import { buildMemberMenuRedirectUrl } from '@campaign/utils/idpfRedirect';
import { createLogger } from '@campaign/utils/logger';

/**
 * Campaign list loads with guards for overlapping async work (login, pagination,
 * reload). Each load: begin → fetch → apply only if still latest →
 * finally clears loading only for the current generation. Pagination reverts
 * optimistic offset on discard
 */
const logger = createLogger('@domain/middleware/user');

function beginCampaignListLoad(store: Store, isLoggedIn: boolean): number {
  let loadGeneration = 0;

  store.swap((state) => {
    loadGeneration = state.loadGenerations.campaignList.generation + 1;

    return {
      ...state,
      errors: {
        ...state.errors,
        campaignList: '',
      },
      loadGenerations: {
        ...state.loadGenerations,
        campaignList: {
          generation: loadGeneration,
          isLoggedIn,
        },
      },
      loading: {
        ...state.loading,
        campaignList: true,
      },
    };
  }, '@begin-campaign-list-load');

  return loadGeneration;
}

function finishCampaignListLoad(store: Store, loadGeneration: number): void {
  const meta = store.deref().loadGenerations.campaignList;

  if (meta.generation !== loadGeneration) {
    return;
  }

  reducers.updateLoading(store, 'campaignList', false);
}

function beginUserCampaignListLoad(store: Store): number {
  let loadGeneration = 0;

  store.swap((state) => {
    loadGeneration = state.loadGenerations.userCampaignList.generation + 1;

    return {
      ...state,
      errors: {
        ...state.errors,
        userCampaignList: '',
      },
      loadGenerations: {
        ...state.loadGenerations,
        userCampaignList: {
          generation: loadGeneration,
        },
      },
      loading: {
        ...state.loading,
        userCampaignList: true,
      },
    };
  }, '@begin-user-campaign-list-load');

  return loadGeneration;
}

function finishUserCampaignListLoad(
  store: Store,
  loadGeneration: number
): void {
  if (
    store.deref().loadGenerations.userCampaignList.generation !== loadGeneration
  ) {
    return;
  }

  reducers.updateLoading(store, 'userCampaignList', false);
}

function shouldApplyCampaignListLoad(
  store: Store,
  loadGeneration: number,
  isLoggedIn: boolean
): boolean {
  const meta = store.deref().loadGenerations.campaignList;

  if (meta.generation !== loadGeneration || meta.isLoggedIn !== isLoggedIn) {
    return false;
  }

  return (
    store.deref().loggedInUser.isLogged === isLoggedIn ||
    (isLoggedIn && Boolean(store.deref().loggedInUser.token))
  );
}

function abortCampaignListLoadIfStale(
  store: Store,
  loadGeneration: number,
  isLoggedIn: boolean
): boolean {
  if (shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
    return false;
  }

  finishCampaignListLoad(store, loadGeneration);
  return true;
}

function shouldApplyUserCampaignListLoad(
  store: Store,
  loadGeneration: number
): boolean {
  if (
    store.deref().loadGenerations.userCampaignList.generation !== loadGeneration
  ) {
    return false;
  }

  return store.deref().loggedInUser.isLogged;
}

function abortUserCampaignListLoadIfStale(
  store: Store,
  loadGeneration: number
): boolean {
  if (shouldApplyUserCampaignListLoad(store, loadGeneration)) {
    return false;
  }

  finishUserCampaignListLoad(store, loadGeneration);
  return true;
}

function revertPaginationOffsetIfStillSelected(
  store: Store,
  key: 'campaignList' | 'userCampaignList',
  selectedOffset: number,
  previousOffset: number
): void {
  if (store.deref().pagination[key].offset === selectedOffset) {
    reducers.updatePaginationOffset(store, key, previousOffset);
  }
}

function clampCampaignListOffset(offset: number, total: number): number {
  if (total <= 0) {
    return 0;
  }

  const maxOffset =
    Math.max(Math.ceil(total / CAMPAIGN_MAX_SIZE) - 1, 0) * CAMPAIGN_MAX_SIZE;

  return Math.min(Math.max(offset, 0), maxOffset);
}

async function resolveCampaignListPage(
  response: NetworkAllCampaigns,
  requestedOffset: number,
  refetch: (offset: number) => Promise<NetworkAllCampaigns>
): Promise<{
  campaigns: NetworkCampaign[];
  offset: number;
  total: number;
}> {
  let total = response.data.total;
  let offset = clampCampaignListOffset(requestedOffset, total);
  let campaigns = response.data.value;

  if (offset !== requestedOffset) {
    const refetchResponse = await refetch(offset);
    total = refetchResponse.data.total;
    offset = clampCampaignListOffset(offset, total);
    campaigns = refetchResponse.data.value;
  }

  return { campaigns, offset, total };
}

export async function onloadCampaigns(store: Store, isLoggedIn: boolean) {
  const loadGeneration = beginCampaignListLoad(store, isLoggedIn);

  if (abortCampaignListLoadIfStale(store, loadGeneration, isLoggedIn)) {
    return;
  }

  try {
    const allCampaignsResponse: NetworkAllCampaigns =
      await network.getCampaigns(isLoggedIn, CAMPAIGN_MAX_SIZE, 0);

    if (!shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
      return;
    }

    reducers.updateCampaignListPage(store, allCampaignsResponse.data.value, {
      limit: CAMPAIGN_MAX_SIZE,
      offset: 0,
      total: allCampaignsResponse.data.total,
    });
  } catch (err) {
    if (!shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
      return;
    }

    logger.error(err);
    reducers.setErrorState(store, 'campaignList', `CAMPAIGN_LIST_ERROR`);
  } finally {
    finishCampaignListLoad(store, loadGeneration);
  }
}

export async function onloadUserCampaigns(store: Store) {
  const loadGeneration = beginUserCampaignListLoad(store);

  if (abortUserCampaignListLoadIfStale(store, loadGeneration)) {
    return;
  }

  try {
    const userCampaignsResponse: NetworkAllCampaigns =
      await network.getUserCampaigns(CAMPAIGN_MAX_SIZE, 0);

    if (!shouldApplyUserCampaignListLoad(store, loadGeneration)) {
      return;
    }

    reducers.updateUserCampaignListPage(
      store,
      userCampaignsResponse.data.value,
      {
        limit: CAMPAIGN_MAX_SIZE,
        offset: 0,
        total: userCampaignsResponse.data.total,
      }
    );
  } catch (err) {
    if (!shouldApplyUserCampaignListLoad(store, loadGeneration)) {
      return;
    }

    logger.error(err);
    reducers.setErrorState(
      store,
      'userCampaignList',
      `USER_CAMPAIGN_LIST_ERROR`
    );
  } finally {
    finishUserCampaignListLoad(store, loadGeneration);
  }
}

export async function onChangeCampaignListPage(store: Store, page: number) {
  const previousOffset = store.deref().pagination.campaignList.offset;
  const offset = (page - 1) * CAMPAIGN_MAX_SIZE;

  if (offset === previousOffset) return;

  const isLoggedIn = store.deref().loggedInUser.isLogged;
  const loadGeneration = beginCampaignListLoad(store, isLoggedIn);

  reducers.updatePaginationOffset(store, 'campaignList', offset);
  try {
    if (abortCampaignListLoadIfStale(store, loadGeneration, isLoggedIn)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'campaignList',
        offset,
        previousOffset
      );
      return;
    }

    const isLoggedInAtFetch = store.deref().loggedInUser.isLogged;

    if (isLoggedInAtFetch !== isLoggedIn) {
      reducers.updatePaginationOffset(store, 'campaignList', previousOffset);
      return;
    }

    const allCampaignsResponse = await network.getCampaigns(
      isLoggedInAtFetch,
      CAMPAIGN_MAX_SIZE,
      offset
    );

    if (!shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'campaignList',
        offset,
        previousOffset
      );
      return;
    }

    const resolvedPage = await resolveCampaignListPage(
      allCampaignsResponse,
      offset,
      (nextOffset) =>
        network.getCampaigns(isLoggedInAtFetch, CAMPAIGN_MAX_SIZE, nextOffset)
    );

    if (!shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'campaignList',
        offset,
        previousOffset
      );
      return;
    }

    reducers.updateCampaignListPage(store, resolvedPage.campaigns, {
      limit: CAMPAIGN_MAX_SIZE,
      offset: resolvedPage.offset,
      total: resolvedPage.total,
    });
  } catch (err) {
    if (!shouldApplyCampaignListLoad(store, loadGeneration, isLoggedIn)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'campaignList',
        offset,
        previousOffset
      );
      return;
    }

    logger.error(err);
    reducers.updatePaginationOffset(store, 'campaignList', previousOffset);
  } finally {
    finishCampaignListLoad(store, loadGeneration);
  }
}

export async function onChangeUserCampaignListPage(store: Store, page: number) {
  const previousOffset = store.deref().pagination.userCampaignList.offset;
  const offset = (page - 1) * CAMPAIGN_MAX_SIZE;

  if (offset === previousOffset) return;

  const loadGeneration = beginUserCampaignListLoad(store);

  reducers.updatePaginationOffset(store, 'userCampaignList', offset);
  try {
    if (abortUserCampaignListLoadIfStale(store, loadGeneration)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'userCampaignList',
        offset,
        previousOffset
      );
      return;
    }

    const userCampaignsResponse = await network.getUserCampaigns(
      CAMPAIGN_MAX_SIZE,
      offset
    );

    if (!shouldApplyUserCampaignListLoad(store, loadGeneration)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'userCampaignList',
        offset,
        previousOffset
      );
      return;
    }

    const resolvedPage = await resolveCampaignListPage(
      userCampaignsResponse,
      offset,
      (nextOffset) => network.getUserCampaigns(CAMPAIGN_MAX_SIZE, nextOffset)
    );

    if (!shouldApplyUserCampaignListLoad(store, loadGeneration)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'userCampaignList',
        offset,
        previousOffset
      );
      return;
    }

    reducers.updateUserCampaignListPage(store, resolvedPage.campaigns, {
      limit: CAMPAIGN_MAX_SIZE,
      offset: resolvedPage.offset,
      total: resolvedPage.total,
    });
  } catch (err) {
    if (!shouldApplyUserCampaignListLoad(store, loadGeneration)) {
      revertPaginationOffsetIfStillSelected(
        store,
        'userCampaignList',
        offset,
        previousOffset
      );
      return;
    }

    logger.error(err);
    reducers.updatePaginationOffset(store, 'userCampaignList', previousOffset);
  } finally {
    finishUserCampaignListLoad(store, loadGeneration);
  }
}

export async function onLogin(store: Store) {
  try {
    await authUtil.initializeSession(store, true);
  } catch (error) {
    logger.error(error);
  }
}

/**
 * onClickLogoutMenuItem
 *
 * Send a request to auth service to logout user and reset the auth state
 * @param store
 */
export async function onClickLogoutMenuItem(store: Store) {
  try {
    await network.logout();
    clearCampaignTabFromUrl();
    location.reload();
  } catch {
    reducers.setErrorModal(store, true, ErrorType.LOG_OUT);
    logger.error({ logData: 'Failed to logout! ' });
  }
}

/**
 * onClickMemberProfileMenuItem
 *
 * redirects to user profile page
 * @param store
 */
export async function onClickMemberProfileMenuItem() {
  try {
    location.assign(buildMemberMenuRedirectUrl());
  } catch {
    logger.error({ logData: 'Failed to go to member profile! ' });
  }
}

export function onClickCloseErrorModal(store: Store) {
  reducers.setErrorModal(store, false, '');
}

/**
 * Load user data (campaigns for the logged-in user).
 *
 * @param store Store
 */
export async function loadUserData(store: Store): Promise<void> {
  try {
    await onloadUserCampaigns(store);
  } catch (error) {
    logger.error('[loadUserData] Failed to load user campaigns', error);
  }
}
